import React, { FormEventHandler, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { RpcError } from 'grpc-web'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { RequirementRenderer } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor'
import { RequirementInfo } from 'src/components/ModalForms/Frameworks/FrameworkSectionsEditor.types'
import {
  useEditFrameworkRequirement,
  useExistingFrameworkNames,
  useFrameworkControls,
} from 'src/pages/Frameworks/frameworks.hooks'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import { ERROR_MESSAGES } from 'src/Utils/globalEnums'
import { parseDuplicateRequirementNameError } from '../AddFramework/FrameworkSectionsCSV.helpers'
import { ModalForm, ModalFormId } from '../..'

type Props = {
  framework: ComplianceFramework
  selectedRequirement: RequirementInfo | null
  clearSelectedRequirement: () => void
}

export const EditFrameworkRequirementModal = ({
  framework,
  selectedRequirement,
  clearSelectedRequirement,
}: Props): JSX.Element => {
  const show = useIsShowModal(ModalFormId.EDIT_FRAMEWORK_REQUIREMENT)
  const hideModal = useHideModal({
    modalId: ModalFormId.EDIT_FRAMEWORK_REQUIREMENT,
  })
  const editFrameworkRequirement = useEditFrameworkRequirement()
  const { controlOptions, isLoading: controlsLoading } =
    useFrameworkControls(show)
  const { existingRequirementNames } = useExistingFrameworkNames(
    framework,
    show,
  )
  const [requirementDraft, setRequirementDraft] =
    useState<RequirementInfo | null>(null)
  const [nameError, setNameError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!show || !selectedRequirement) return
    setRequirementDraft(selectedRequirement)
    setNameError('')
    setIsSubmitting(false)
  }, [selectedRequirement, show])

  // Names of every other requirement in the framework, lowercased for the
  // collision check below. The requirement being edited is excluded so a
  // no-op rename doesn't trip the guard.
  const otherRequirementNames = useMemo(
    () =>
      existingRequirementNames
        .filter((n) => n !== selectedRequirement?.name)
        .map((n) => n.trim().toLowerCase())
        .filter(Boolean),
    [existingRequirementNames, selectedRequirement],
  )

  const closeAndReset = () => {
    hideModal()
    setRequirementDraft(null)
    setNameError('')
    setIsSubmitting(false)
    clearSelectedRequirement()
  }

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault()
    if (!selectedRequirement || !requirementDraft) return
    const trimmedName = requirementDraft.name.trim()
    if (!trimmedName) {
      setNameError(ERROR_MESSAGES.EMPTY)
      return
    }
    if (otherRequirementNames.includes(trimmedName.toLowerCase())) {
      setNameError(
        `Requirement "${trimmedName}" already exists in this framework.`,
      )
      return
    }
    setNameError('')
    setIsSubmitting(true)
    try {
      if (!requirementDraft.modelId) {
        throw new Error('Cannot edit a requirement without a model_id')
      }
      await editFrameworkRequirement({
        requirementModelId: requirementDraft.modelId,
        name: requirementDraft.name,
        description: requirementDraft.description,
        controlIds: requirementDraft.controlIds,
      })
      toast.success('Requirement updated.')
      closeAndReset()
    } catch (err) {
      const rpcErr = err as RpcError
      const dupInfo = parseDuplicateRequirementNameError(rpcErr?.message ?? '')
      if (dupInfo) {
        setNameError(
          dupInfo.isExisting
            ? `Requirement "${dupInfo.name}" already exists in this framework.`
            : `Duplicate requirement name "${dupInfo.name}".`,
        )
        return
      }
      const errorMessage = getErrorMessage(rpcErr)
      toast.error(errorMessage.message)
      if (errorMessage.shouldLog) {
        log.error('EditFrameworkRequirementModal submit failed', err)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ModalForm
      show={show}
      hide={closeAndReset}
      formId={ModalFormId.EDIT_FRAMEWORK_REQUIREMENT}
      size="lg"
      title="Edit Requirement"
      submitText="Save"
      showOverflow
      disableSubmitButton={
        isSubmitting ||
        controlsLoading ||
        !selectedRequirement ||
        !requirementDraft
      }
    >
      <form id={ModalFormId.EDIT_FRAMEWORK_REQUIREMENT} onSubmit={onSubmit}>
        {requirementDraft && (
          <RequirementRenderer
            index={0}
            requirement={requirementDraft}
            controlOptions={controlOptions}
            nameError={nameError}
            onUpdate={(patch) => {
              if (nameError && patch.name !== undefined) setNameError('')
              setRequirementDraft((prev) =>
                prev ? { ...prev, ...patch } : prev,
              )
            }}
            hideRowHeader
          />
        )}
      </form>
    </ModalForm>
  )
}
